import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Download, Send, Users, Pencil, Trash2, X, Save, Clock } from "lucide-react";

// Convert a local datetime-local string to ISO with UTC offset
const localToIso = (localStr) => {
  if (!localStr) return null;
  // datetime-local yields "YYYY-MM-DDTHH:mm" in local tz
  const d = new Date(localStr);
  return isNaN(d.getTime()) ? null : d.toISOString();
};
// Convert an ISO stored value back into "YYYY-MM-DDTHH:mm" local
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function BroadcastsPanel() {
  const [stats, setStats] = useState({ total: 0, active: 0, subscribers: [] });
  const [broadcasts, setBroadcasts] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [epNum, setEpNum] = useState("");
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/subscribers"),
        api.get("/admin/broadcasts"),
      ]);
      setStats(s.data);
      setBroadcasts(b.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setSubject(""); setBody(""); setEpNum(""); setScheduledLocal(""); setEditingId(null);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const payload = { subject, body };
      if (epNum) payload.episode_number = parseInt(epNum, 10);
      if (scheduledLocal) payload.scheduled_at = localToIso(scheduledLocal);
      if (editingId) {
        await api.put(`/admin/broadcasts/${editingId}`, payload);
        toast.success(scheduledLocal ? "scheduled" : "draft updated");
      } else {
        await api.post("/admin/broadcasts", payload);
        toast.success(scheduledLocal ? "scheduled ♡" : "draft saved · " + stats.active + " subscribers ready");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not save draft");
    } finally { setBusy(false); }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setSubject(b.subject || "");
    setBody(b.body || "");
    setEpNum(b.episode_number ? String(b.episode_number) : "");
    setScheduledLocal(b.scheduled_at ? isoToLocalInput(b.scheduled_at) : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.subject}"?`)) return;
    try {
      await api.delete(`/admin/broadcasts/${b.id}`);
      toast.success("deleted");
      if (editingId === b.id) resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not delete");
    }
  };

  const post = async (b) => {
    if (!window.confirm(`Post "${b.subject}" to ${stats.active} subscribers right now?`)) return;
    try {
      const res = await api.post(`/admin/broadcasts/${b.id}/send`);
      const { delivered_count = 0, failed_count = 0, note } = res?.data || {};
      if (failed_count === 0 && delivered_count > 0) {
        toast.success(`sent to ${delivered_count} ♡`);
      } else if (delivered_count > 0) {
        toast.success(`${delivered_count} delivered · ${failed_count} failed`);
      } else {
        toast.error(note || "all sends failed — check backend logs");
      }
      if (note) console.log("[broadcast]", note);
      if (editingId === b.id) resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not post");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/subscribers.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "if-we-knew-subscribers.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("export failed"); }
  };

  const scheduled = broadcasts.filter(b => b.status === "scheduled" || b.status === "sending");
  const drafts = broadcasts.filter(b => b.status === "draft");
  const posted = broadcasts.filter(b => b.status === "posted");

  return (
    <div className="mb-10" data-testid="broadcasts-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Megaphone className="text-hotpink" size={22} />
        <h2 className="brush-title text-3xl md:text-4xl text-white">THE <span className="text-hotpink">LIST.</span></h2>
        <span className="scribble text-hotpink text-lg ml-auto">{stats.active} subscribers ♡</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-coal border border-white/10 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 font-outfit">
            <Users size={14} className="text-hotpink" /> mailing list
          </div>
          <div className="brush-title text-5xl text-white mt-1">{stats.active}</div>
          <div className="text-white/40 text-xs uppercase tracking-widest font-outfit">{stats.total} total signups</div>
          <button onClick={downloadCsv} data-testid="export-subscribers" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-outfit font-bold text-white border border-white/20 hover:border-hotpink hover:text-hotpink px-4 py-2 transition-colors">
            <Download size={12} /> export csv
          </button>
        </div>

        <div className="bg-coal border border-hotpink/40 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 font-outfit mb-3">
            <Send size={14} className="text-hotpink"/> {editingId ? "edit broadcast" : "compose broadcast"}
            {editingId && (
              <button type="button" onClick={resetForm} className="ml-auto text-white/50 hover:text-hotpink flex items-center gap-1 text-[10px]" data-testid="broadcast-cancel-edit">
                <X size={12} /> cancel
              </button>
            )}
          </div>
          <form onSubmit={save} className="space-y-2" data-testid="broadcast-form">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="subject · 'Ep 11 is live'" required maxLength={200} data-testid="broadcast-subject" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="body · the chaos of the week..." required maxLength={10000} rows={4} data-testid="broadcast-body" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none resize-none" />
            <div className="flex items-center gap-2 flex-wrap">
              <input value={epNum} onChange={(e) => setEpNum(e.target.value)} type="number" placeholder="ep #" min="1" max="999" data-testid="broadcast-ep" className="w-20 bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
              <label className="flex items-center gap-2 flex-1 min-w-[220px] bg-ink border border-white/15 px-3 py-2">
                <Clock size={13} className="text-hotpink/70 flex-shrink-0"/>
                <input value={scheduledLocal} onChange={(e) => setScheduledLocal(e.target.value)} type="datetime-local" data-testid="broadcast-schedule" className="flex-1 bg-transparent text-white font-outfit text-xs focus:outline-none [color-scheme:dark]" />
                {scheduledLocal && (
                  <button type="button" onClick={() => setScheduledLocal("")} className="text-white/40 hover:text-hotpink" data-testid="broadcast-clear-schedule"><X size={12}/></button>
                )}
              </label>
              <button type="submit" disabled={busy || !subject.trim() || !body.trim()} data-testid="broadcast-save" className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60 flex items-center gap-2">
                <Save size={12} /> {busy ? "..." : (scheduledLocal ? "schedule" : (editingId ? "update" : "save draft"))}
              </button>
            </div>
            <p className="text-[10px] text-white/40 uppercase tracking-widest font-outfit">leave the datepicker empty to save as draft · set a time to auto-post via resend</p>
          </form>
        </div>
      </div>

      {scheduled.length > 0 && (
        <div className="bg-coal border border-hotpink/30 p-5 mb-4">
          <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
            <Clock size={14} className="text-hotpink" /> Scheduled <span className="text-hotpink">({scheduled.length})</span>
          </h3>
          <div className="space-y-3">
            {scheduled.map(b => (
              <div key={b.id} className={"border-b border-white/5 pb-3 text-sm font-outfit " + (editingId === b.id ? "bg-hotpink/5 -mx-2 px-2 py-2 border border-hotpink/40" : "")} data-testid={"broadcast-scheduled-" + b.id.slice(0, 8)}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-white font-bold">{b.subject}</span>
                  {b.episode_number && <span className="scribble text-hotpink text-base">· Ep {b.episode_number}</span>}
                  <span className="text-hotpink text-[10px] uppercase tracking-widest font-bold">
                    {b.status === "sending" ? "SENDING..." : "SCHEDULED · " + new Date(b.scheduled_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-white/70 text-xs mt-1 line-clamp-2">{b.body}</p>
                {b.status !== "sending" && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button onClick={() => startEdit(b)} data-testid={"broadcast-edit-" + b.id.slice(0, 8)} className="text-[10px] uppercase tracking-widest font-outfit font-bold text-white border border-white/20 hover:border-hotpink hover:text-hotpink px-3 py-1 flex items-center gap-1 transition-colors">
                      <Pencil size={11} /> edit
                    </button>
                    <button onClick={() => post(b)} data-testid={"broadcast-post-" + b.id.slice(0, 8)} className="text-[10px] uppercase tracking-widest font-outfit font-bold bg-hotpink hover:bg-hotpink_dark text-white px-3 py-1 flex items-center gap-1 transition-colors">
                      <Send size={11} /> post now
                    </button>
                    <button onClick={() => remove(b)} data-testid={"broadcast-delete-" + b.id.slice(0, 8)} className="text-[10px] uppercase tracking-widest font-outfit font-bold text-white/70 hover:text-red-500 px-3 py-1 flex items-center gap-1 ml-auto transition-colors">
                      <Trash2 size={11} /> cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="bg-coal border border-white/10 p-5 mb-4">
          <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest mb-3">Drafts <span className="text-hotpink">({drafts.length})</span></h3>
          <div className="space-y-3">
            {drafts.map(b => (
              <div key={b.id} className={"border-b border-white/5 pb-3 text-sm font-outfit " + (editingId === b.id ? "bg-hotpink/5 -mx-2 px-2 py-2 border border-hotpink/40" : "")} data-testid={"broadcast-draft-" + b.id.slice(0, 8)}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-white font-bold">{b.subject}</span>
                  {b.episode_number && <span className="scribble text-hotpink text-base">· Ep {b.episode_number}</span>}
                  <span className="text-white/40 text-[10px] uppercase tracking-widest">DRAFT · {b.recipient_count} recipients · {new Date(b.updated_at || b.created_at).toLocaleString()}</span>
                </div>
                <p className="text-white/70 text-xs mt-1 line-clamp-2">{b.body}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button onClick={() => startEdit(b)} data-testid={"broadcast-edit-" + b.id.slice(0, 8)} className="text-[10px] uppercase tracking-widest font-outfit font-bold text-white border border-white/20 hover:border-hotpink hover:text-hotpink px-3 py-1 flex items-center gap-1 transition-colors">
                    <Pencil size={11} /> edit
                  </button>
                  <button onClick={() => post(b)} data-testid={"broadcast-post-" + b.id.slice(0, 8)} className="text-[10px] uppercase tracking-widest font-outfit font-bold bg-hotpink hover:bg-hotpink_dark text-white px-3 py-1 flex items-center gap-1 transition-colors">
                    <Send size={11} /> post now
                  </button>
                  <button onClick={() => remove(b)} data-testid={"broadcast-delete-" + b.id.slice(0, 8)} className="text-[10px] uppercase tracking-widest font-outfit font-bold text-white/70 hover:text-red-500 px-3 py-1 flex items-center gap-1 ml-auto transition-colors">
                    <Trash2 size={11} /> delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {posted.length > 0 && (
        <div className="bg-coal border border-white/10 p-5">
          <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest mb-3">Posted <span className="text-white/40">({posted.length})</span></h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {posted.map(b => (
              <div key={b.id} className="border-b border-white/5 pb-2 text-sm font-outfit" data-testid={"broadcast-posted-" + b.id.slice(0, 8)}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-white font-bold">{b.subject}</span>
                  {b.episode_number && <span className="scribble text-hotpink text-base">· Ep {b.episode_number}</span>}
                  <span className="text-white/40 text-[10px] uppercase tracking-widest ml-auto">
                    POSTED · {b.delivered_count ?? b.recipient_count} sent{b.failed_count ? ` · ${b.failed_count} failed` : ""} · {new Date(b.posted_at || b.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-white/70 text-xs mt-1 line-clamp-2">{b.body}</p>
                {b.delivery_error && <p className="text-red-400 text-[10px] font-outfit mt-1">error: {b.delivery_error}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {drafts.length === 0 && posted.length === 0 && scheduled.length === 0 && (
        <p className="text-white/40 font-outfit text-sm italic">no broadcasts yet — save your first draft above ↑</p>
      )}
    </div>
  );
}
